/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * TagRankPicker.jsx — เลือกสินค้าเข้าป้ายตาม หมวด → หมวดย่อย → A/B/C จากยอดขายสาขา (29 ต.ค. 2569 · ประชุมพี่นก 28 ต.ค.)
 *   อยู่ในหน้าต่าง "+ เพิ่ม" ของ Tag.jsx ใต้สาขา/แม่แบบ · ไม่เลือกหมวด = สร้างป้ายเปล่าแบบเดิม
 *   OPTIONS /tag_rank/options · /tag_rank/preview (นับ A/B/C) · /tag_rank/create (createRankTags)
 *   ค่าที่ผู้ใช้เลือกอยู่ใน Tag.jsx (value/onChange) ผลนับส่งกลับทาง onStatus → Tag.jsx ใช้ตัดสินว่ากดบันทึกได้ไหม
 *   หรืออัปไฟล์รหัสสินค้า (31 ต.ค. 2569 · พี่นก เปิดสาขาใหม่พันกว่ารายการ): /tag_rank/import_preview → value.file
 *   → Tag.jsx สร้างด้วย createImportTags (ป้ายเรียงตามแถวในไฟล์)
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';

import { FetchData } from 'services/Request';

/* subcat_id '0' = ทุกหมวดย่อย (ค่าว่างทำให้ช่องเลือกไม่แสดงข้อความตั้งต้น)
   sheets = จะพิมพ์กี่แผ่น (ดูหน้าชั้นแล้วเลือก · 29 ต.ค. 2569) → เอาตัวขายดีสุด แผ่น × ป้ายต่อแผ่น · '0' = ทั้งหมดในเกรดที่เลือก */
export const RANK_EMPTY = {
  cat_id: '',
  subcat_id: '0',
  months: '3',
  abc: ['A'],
  sheets: '0',
  file: null, // ผลตรวจไฟล์รหัสสินค้า (import_preview) + name
};
const MAX_SHEETS = 10;
const FILE_MAX_BYTES = 2 * 1024 * 1024; // เท่า tag_rank.con.php (ไฟล์ 3,000 รายการจริง ~100 KB)
const FILE_SHOW = 8; // รหัสที่ไม่พบ โชว์กี่ตัวก่อนกด "ดูทั้งหมด"

const LETTERS = ['A', 'B', 'C'];
const MONTHS = ['1', '3', '6'];
const LOGIN_EXPIRED = 'หมดเวลาเข้าสู่ระบบ เข้าสู่ระบบใหม่แล้วลองอีกครั้ง';

export const RANK_ERROR_TEXT = {
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์ใช้เมนู TAG',
  rank_too_many: 'สินค้าเกิน 1,000 รายการ เลือกหมวดย่อย หรือติ๊กเกรดให้น้อยลง',
  rank_no_products: 'ไม่มีสินค้าในเกรดที่เลือก',
  abc_required: 'ติ๊ก A, B หรือ C อย่างน้อย 1 ตัว',
  category_required: 'เลือกหมวดก่อน',
  branch_required: 'เลือกสาขาก่อน',
  branch_not_found: 'ไม่พบสาขานี้',
  category_not_found: 'ไม่พบหมวดนี้',
  subcat_not_found: 'หมวดย่อยนี้ไม่อยู่ในหมวดที่เลือก',
  import_file_type: 'ใช้ไฟล์ Excel (.xlsx .xls) หรือ .csv',
  import_file_too_large: 'ไฟล์ใหญ่เกิน 2 MB ลบคอลัมน์/ชีตที่ไม่ใช้ออก หรือแบ่งไฟล์',
  import_file_invalid: 'เปิดไฟล์นี้ไม่ได้ ลองเซฟจาก Excel ใหม่เป็น .xlsx',
  import_no_codes: 'ไม่พบรหัสสินค้าหรือบาร์โค้ดที่ตรงกับระบบในไฟล์นี้',
  import_no_products: 'ไม่มีสินค้าในไฟล์ที่ทำป้ายได้',
  import_too_many: 'สินค้าเกิน 3,000 รายการ แบ่งเป็น 2 ไฟล์',
  not_login: LOGIN_EXPIRED,
};

export const rankErrorText = (result) =>
  RANK_ERROR_TEXT[result && result.msg] || 'สร้างป้ายไม่สำเร็จ ลองอีกครั้ง';

/** สร้างป้ายจากตัวเลือก — คืน {status, tags:[{id,code,count,set}], total} เรียงชุด 1 → n */
export const createRankTags = (token, payload) =>
  FetchData('OPTIONS', '/tag_rank/create', token, { ...payload, _t: Date.now() });

/** สร้างป้ายจากไฟล์ที่ตรวจแล้ว — payload {branch_id, template_id, remark, product_ids, name} คืนแบบเดียวกับ createRankTags */
export const createImportTags = (token, payload) =>
  FetchData('OPTIONS', '/tag_rank/import_create', token, { ...payload, _t: Date.now() });

const num = (v) => Number(v || 0).toLocaleString('th-TH');

/**
 * รวมชีตที่ติ๊กไว้ (file.picked) → สินค้าเรียงตามไฟล์ ตัดซ้ำข้ามชีต + ยอดแจ้งเตือน
 * ใช้ทั้งหน้าจอนับและ Tag.jsx ตอนสร้าง (ids ชุดเดียวกัน)
 */
export const fileMerge = (file) => {
  const out = { ids: [], rows: 0, dup: 0, multi: 0, hidden: 0, blank: 0, unwanted: 0 };
  const miss = new Set();
  const shut = new Set();
  let missSum = 0;
  let shutSum = 0;
  let complete = true; // รายการไม่พบ/ปิดขายครบทุกตัว (ไม่ถูกตัดที่ 300) = นับจากรายการรวมได้ตรง
  if (!file) return { ...out, missing: [], missingCount: 0, closed: [], closedCount: 0 };
  const seen = new Set();
  (file.sheets || []).forEach((s, i) => {
    if (!(file.picked || [])[i]) return;
    /* ข้ามแถวที่ช่อง "ต้องการ" ว่าง (ไม่ได้สั่งเข้าสาขา) — ชีตที่มีคอลัมน์นี้ใช้ want_ids (ไฟล์ร้าน MBK · 2 พ.ย.) */
    const skip = file.skipUnwanted && s.want_col;
    if (skip) out.unwanted += Number(s.unwanted || 0);
    (skip ? s.want_ids || [] : s.ids).forEach((id) => {
      if (seen.has(id)) out.dup += 1;
      else {
        seen.add(id);
        out.ids.push(id);
      }
    });
    /* ข้ามแถวที่ไม่ได้สั่ง = รหัสไม่พบ/ปิดขายนับเฉพาะแถวที่สั่ง ไม่ต้องไปตามรหัสที่ไม่ได้ใช้ */
    const missing = (skip ? s.missing_want : s.missing) || [];
    const closed = (skip ? s.closed_want : s.closed) || [];
    const missCount = Number((skip ? s.missing_want_count : s.missing_count) || 0);
    const shutCount = Number((skip ? s.closed_want_count : s.closed_count) || 0);
    missing.forEach((x) => miss.add(x));
    closed.forEach((x) => shut.add(x));
    missSum += missCount;
    shutSum += shutCount;
    complete = complete && missing.length === missCount && closed.length === shutCount;
    ['rows', 'dup', 'multi', 'hidden', 'blank'].forEach((k) => {
      out[k] += Number(s[k] || 0);
    });
  });
  return {
    ...out,
    missing: [...miss],
    missingCount: complete ? miss.size : missSum,
    closed: [...shut],
    closedCount: complete ? shut.size : shutSum,
  };
};

/** ป้ายต่อแผ่นของแม่แบบ (SINGLE = 1 · ไม่เลือก = ค่าตั้งต้น A4 16 ดวง) */
export const perPageOf = (tpl) =>
  !tpl || tpl.paper !== 'SINGLE'
    ? Number((tpl && tpl.cols) || 4) * Number((tpl && tpl.rows) || 4)
    : 1;

/** ภาพย่อแผ่นกระดาษ: วางป้ายตามแม่แบบ ให้เห็นก่อนว่าแผ่นหนึ่งได้กี่ใบ ทรงไหน (29 ต.ค. 2569 · เลือกขนาดก่อน) */
export function SheetPreview({ tpl }) {
  /* ไม่เลือกแม่แบบ = ค่าตั้งต้น A4 แนวนอน 4×4 (เดิมโชว์ 1 ป้าย/แผ่น ขัดกับคำในช่อง "16 ดวง" · เดินหน้าจอ 29 ต.ค.) */
  const grid = !tpl || tpl.paper !== 'SINGLE';
  const cols = grid ? Number((tpl && tpl.cols) || 4) : 1;
  const rows = grid ? Number((tpl && tpl.rows) || 4) : 1;
  const land = tpl ? tpl.orientation === 'L' : true; // ค่าตั้งต้น A4 แนวนอน
  const promo = tpl && tpl.layout === 'PROMO';
  const paper = (tpl && tpl.paper) || 'A4';
  const size =
    tpl && Number(tpl.width_mm) && promo
      ? ` · ป้ายละ ${Number(tpl.width_mm) / 10}×${Number(tpl.height_mm) / 10} ซม.`
      : '';
  return (
    <MDBox display='flex' alignItems='center' gap={2} mt={1}>
      <MDBox
        aria-hidden
        sx={{
          width: land ? 92 : 66,
          height: land ? 66 : 92,
          border: '1px solid',
          borderColor: 'grey.400',
          bgcolor: '#fff',
          p: '4px',
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: '2px',
          flexShrink: 0,
        }}
      >
        {Array.from({ length: cols * rows }).map((_, i) => (
          <MDBox
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            sx={{
              border: promo ? '1px solid #E22320' : '1px solid #9e9e9e',
              borderTop: promo ? '3px solid #E22320' : undefined,
              borderRadius: promo ? '2px' : 0,
            }}
          />
        ))}
      </MDBox>
      <MDTypography variant='caption' color='text'>
        {paper === 'SINGLE' ? 'พิมพ์ทีละใบ' : `กระดาษ ${paper} · ${num(cols * rows)} ป้าย/แผ่น`}
        {size}
      </MDTypography>
    </MDBox>
  );
}

SheetPreview.defaultProps = { tpl: null };
SheetPreview.propTypes = {
  tpl: PropTypes.shape({
    paper: PropTypes.string,
    cols: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orientation: PropTypes.string,
    layout: PropTypes.string,
    width_mm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height_mm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

/* จำนวนหน้ากระดาษรวมทุกชุด — แต่ละชุดขึ้นหน้าใหม่ จึงคิดทีละชุด (รีวิว 29 ต.ค.) */
const pageCount = (total, perSet, perPage) => {
  let pages = 0;
  for (let left = total; left > 0; left -= perSet)
    pages += Math.ceil(Math.min(left, perSet) / perPage);
  return pages;
};

function TagRankPicker({
  open,
  token,
  branchId,
  templates,
  templateId,
  value,
  onChange,
  onStatus,
  mode,
}) {
  const [opt, setOpt] = useState({ categories: [], groups: [], max_per_tag: 200, max_total: 1000 });
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const seq = useRef(0);
  const fileSeq = useRef(0);
  const fileInput = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const { file } = value;

  /* เปิด/ปิดหน้าต่าง = ทิ้งการอ่านไฟล์ที่ค้าง ไม่ให้ผลมาเติมหลัง Tag.jsx ล้างค่าแล้ว */
  useEffect(() => {
    fileSeq.current += 1;
    setFileBusy(false);
    setFileError('');
  }, [open]);

  const readFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ''; // เลือกไฟล์เดิมซ้ำได้ (แก้ไฟล์แล้วอัปใหม่)
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) {
      setFileError(RANK_ERROR_TEXT.import_file_type);
      return;
    }
    if (f.size > FILE_MAX_BYTES) {
      setFileError(RANK_ERROR_TEXT.import_file_too_large);
      return;
    }
    fileSeq.current += 1;
    const mine = fileSeq.current;
    const fail = (text) => {
      if (!alive.current || mine !== fileSeq.current) return;
      setFileBusy(false);
      setFileError(file ? `${text} · ยังใช้ไฟล์เดิม ${file.name}` : text);
    };
    setFileBusy(true);
    setFileError('');
    const reader = new FileReader();
    reader.onerror = () => fail(RANK_ERROR_TEXT.import_file_invalid);
    reader.onload = () => {
      FetchData('OPTIONS', '/tag_rank/import_preview', token, {
        name: f.name,
        filedata: reader.result,
        _t: Date.now(),
      })
        .then((r) => {
          if (!alive.current || mine !== fileSeq.current) return;
          if (!r || r.status !== 'ok') {
            fail(RANK_ERROR_TEXT[r && r.msg] || 'อ่านไฟล์ไม่สำเร็จ ลองอีกครั้ง');
            return;
          }
          setFileBusy(false);
          setShowAll(false);
          onChange({
            ...value,
            cat_id: '',
            subcat_id: '0',
            file: {
              ...r,
              name: f.name,
              picked: (r.sheets || []).map((x) => Boolean(x.picked)),
              skipUnwanted: (r.sheets || []).some((x) => x.want_col), // ค่าตั้งต้น = ข้ามแถวที่ไม่ได้สั่ง
            },
          });
        })
        .catch(() => fail('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง'));
    };
    reader.readAsDataURL(f);
  };

  useEffect(() => {
    alive.current = true;
    /* โหลดหมวดไม่สำเร็จ = ช่องหมวดว่าง สร้างป้ายเปล่าได้ตามเดิม (ไม่ปล่อย promise ค้างเป็น error ทั้งหน้า) */
    FetchData('OPTIONS', '/tag_rank/options', token, { _t: Date.now() })
      .then((r) => {
        if (alive.current && r && r.status === 'ok') setOpt(r);
      })
      .catch(() => {});
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, [token]);

  /* นับ A/B/C ใหม่ทุกครั้งที่เปลี่ยนสาขา/หมวด/หมวดย่อย/ช่วงเวลา · คำตอบเก่าที่มาช้าไม่ทับของใหม่ (seq)
     หน้าต่างปิด (keepMounted) ไม่นับ — หน้า /tag/:id ใช้คอมโพเนนต์เดียวกัน เปลี่ยนสาขาในหน้าป้ายเคยยิงนับทุกครั้ง (รีวิว 29 ต.ค.) */
  useEffect(() => {
    clearTimeout(timer.current);
    seq.current += 1;
    setCounts(null);
    setError('');
    if (!open || !value.cat_id || !branchId) {
      setLoading(false);
      return;
    }
    const mine = seq.current;
    setLoading(true);
    timer.current = setTimeout(() => {
      FetchData('OPTIONS', '/tag_rank/preview', token, {
        branch_id: branchId,
        cat_id: value.cat_id,
        subcat_id: value.subcat_id || 0,
        months: value.months,
        _t: Date.now(),
      })
        .then((r) => {
          if (!alive.current || mine !== seq.current) return;
          setLoading(false);
          if (r && r.status === 'ok') setCounts(r);
          else if (r && RANK_ERROR_TEXT[r.msg]) setError(RANK_ERROR_TEXT[r.msg]);
          else setError('นับสินค้าไม่สำเร็จ');
        })
        .catch(() => {
          if (!alive.current || mine !== seq.current) return;
          setLoading(false);
          setError('นับสินค้าไม่สำเร็จ');
        });
    }, 300);
  }, [open, token, branchId, value.cat_id, value.subcat_id, value.months, retry]);

  const tpl = templates.find((x) => String(x.id) === String(templateId));
  const perPage = perPageOf(tpl);
  const inGrades = counts ? value.abc.reduce((s, k) => s + Number(counts.counts[k] || 0), 0) : 0;
  const sheets = Number(value.sheets || 0);
  const limit = sheets > 0 ? sheets * perPage : 0;
  const picked = limit > 0 ? Math.min(inGrades, limit) : inGrades;
  const tooMany = picked > Number(opt.max_total || 1000);
  /* เกรดที่ยังไม่ติ๊กแต่มีสินค้า — ใช้แนะนำเมื่อเกรดที่ติ๊กว่าง (สินค้าตัวเดียวที่ขายในหมวดย่อยได้ 100% = C ตามสูตรรายงาน) */
  const others = counts
    ? LETTERS.filter((k) => !value.abc.includes(k) && Number(counts.counts[k] || 0) > 0)
    : [];
  const emptyText = () => {
    if (!value.abc.length) return RANK_ERROR_TEXT.abc_required;
    if (counts && Number(counts.total || 0) === 0)
      return value.months === '6'
        ? 'หมวดนี้ไม่มีสินค้าที่ขายได้ในช่วงนี้'
        : 'หมวดนี้ไม่มีสินค้าที่ขายได้ในช่วงนี้ ลองยอดขาย 6 เดือน';
    return others.length
      ? `${RANK_ERROR_TEXT.rank_no_products} ลองติ๊ก ${others.join(' หรือ ')}`
      : RANK_ERROR_TEXT.rank_no_products;
  };

  /* ส่งสถานะให้ Tag.jsx: ไม่เลือกหมวด/ไฟล์ = ป้ายเปล่าแบบเดิม · เลือกหมวดแล้วต้องนับเสร็จและได้ 1–1,000 รายการ
     ไฟล์ = 1–3,000 สินค้าที่ทำป้ายได้ · ระหว่างอ่านไฟล์ห้ามบันทึก (ไม่งั้นได้ป้ายเปล่า) */
  const merged = fileMerge(file);
  const fileTotal = merged.ids.length;
  const fileMax = file ? Number(file.max_import || 3000) : 3000;
  useEffect(() => {
    let reason = '';
    /* อ่านไฟล์ (รวมตอนเปลี่ยนไฟล์) ก่อนดูไฟล์เดิม — ไม่งั้นกดยืนยันได้ด้วยไฟล์เก่าระหว่างอัปไฟล์ใหม่ (ผู้ตรวจ 31 ต.ค.) */
    if (fileBusy) {
      reason = 'กำลังอ่านไฟล์ รอสักครู่';
    } else if (file) {
      if (!branchId) reason = RANK_ERROR_TEXT.branch_required;
      else if (fileTotal === 0) reason = RANK_ERROR_TEXT.import_no_products;
      else if (fileTotal > fileMax) reason = RANK_ERROR_TEXT.import_too_many;
    } else if (value.cat_id) {
      if (!branchId) reason = RANK_ERROR_TEXT.branch_required;
      else if (error) reason = error;
      else if (loading || !counts) reason = 'กำลังนับสินค้า รอสักครู่';
      else if (picked === 0) reason = emptyText();
      else if (tooMany) reason = RANK_ERROR_TEXT.rank_too_many;
    }
    onStatus({
      active: Boolean(value.cat_id || file || fileBusy),
      reason,
      total: file ? fileTotal : picked,
      limit: file ? 0 : limit,
      busy: fileBusy,
    });
  }, [
    value.cat_id,
    value.abc,
    value.months,
    file,
    fileTotal,
    fileBusy,
    branchId,
    loading,
    counts,
    error,
    picked,
    tooMany,
    limit,
  ]);

  /* ชุดละเต็มแผ่น เท่ากับ tag_rank.con.php — 200 ปัดลงเป็นจำนวนแผ่นเต็ม (16/แผ่น = 192) */
  const perSet = Math.max(perPage, Math.floor(Number(opt.max_per_tag || 200) / perPage) * perPage);
  const pages = pageCount(picked, perSet, perPage);
  const sets = Math.ceil(picked / perSet);
  const filePages = pageCount(fileTotal, perSet, perPage);
  const fileSets = Math.ceil(fileTotal / perSet);
  const groups = opt.groups.filter((g) => String(g.cat_id) === String(value.cat_id));

  const toggle = (k) =>
    onChange({
      ...value,
      abc: value.abc.includes(k)
        ? value.abc.filter((x) => x !== k)
        : LETTERS.filter((x) => x === k || value.abc.includes(x)),
    });

  const summary = () => {
    if (!value.cat_id) return null;
    if (!branchId)
      return (
        <MDTypography variant='caption' color='warning'>
          {RANK_ERROR_TEXT.branch_required}
        </MDTypography>
      );
    if (error)
      return (
        <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
          <MDTypography variant='caption' color='error'>
            {error}
          </MDTypography>
          {error !== LOGIN_EXPIRED ? (
            <MDButton
              size='small'
              variant='text'
              color='info'
              onClick={() => setRetry((n) => n + 1)}
            >
              <Icon>refresh</Icon>&nbsp;ลองนับใหม่
            </MDButton>
          ) : null}
        </MDBox>
      );
    if (loading || !counts)
      return (
        <MDBox display='flex' alignItems='center' gap={1}>
          <CircularProgress size={14} />
          <MDTypography variant='caption' color='text'>
            กำลังนับสินค้า…
          </MDTypography>
        </MDBox>
      );
    if (tooMany)
      return (
        <MDTypography variant='caption' color='error' fontWeight='medium'>
          ได้ {num(picked)} รายการ เกินเพดาน {num(opt.max_total)} · เลือกหมวดย่อย
          หรือติ๊กเกรดให้น้อยลง
        </MDTypography>
      );
    if (picked === 0)
      return (
        <MDTypography variant='caption' color='warning'>
          {emptyText()}
        </MDTypography>
      );
    return (
      <MDTypography variant='caption' color='text' fontWeight='medium'>
        ได้ {num(picked)} สินค้า
        {limit > 0 && inGrades > picked ? ` (ขายดีสุดจาก ${num(inGrades)})` : ''}
        {' · '}
        {num(pages)} หน้า
        {sets > 1 ? ` · แบ่ง ${sets} ชุด (ชุดละไม่เกิน ${num(perSet)})` : ''}
      </MDTypography>
    );
  };

  /* รหัสที่ทำป้ายไม่ได้ — โชว์ไม่กี่ตัว กด "ดูทั้งหมด" แล้วเลื่อนดู/คัดลอกไปแก้ไฟล์ได้ */
  const codeLine = (label, list, count) => {
    if (!count) return null;
    const shown = showAll ? list : list.slice(0, FILE_SHOW);
    const more = count - shown.length;
    return (
      <MDBox mt={0.5}>
        <MDTypography variant='caption' color='warning' fontWeight='medium'>
          {label} {num(count)} รหัส:
        </MDTypography>{' '}
        <MDTypography
          variant='caption'
          color='text'
          component='span'
          sx={
            showAll
              ? { display: 'block', maxHeight: 96, overflowY: 'auto', wordBreak: 'break-all' }
              : { wordBreak: 'break-all' }
          }
        >
          {shown.join(', ')}
          {more > 0 && showAll ? ` และอีก ${num(more)}` : ''}
        </MDTypography>
        {more > 0 && !showAll ? (
          <MDButton
            size='small'
            variant='text'
            color='info'
            sx={{ p: 0, minHeight: 0, ml: 0.5 }}
            onClick={() => setShowAll(true)}
          >
            ดูทั้งหมด
          </MDButton>
        ) : null}
      </MDBox>
    );
  };

  const bookSheets = file ? file.sheets || [] : [];
  const togglePick = (i) =>
    onChange({
      ...value,
      file: { ...file, picked: file.picked.map((on, k) => (k === i ? !on : on)) },
    });
  /* แถวที่ข้าม — บอกให้รู้ ไม่หายเงียบ */
  const notes = [
    merged.dup > 0 ? `ซ้ำในไฟล์ ${num(merged.dup)} แถว (ทำป้ายใบเดียว)` : '',
    merged.multi > 0 ? `${num(merged.multi)} รหัสตรงหลายสินค้า (ทำป้ายทุกตัว)` : '',
    merged.hidden > 0 ? `ข้ามแถวที่ซ่อนไว้ใน Excel ${num(merged.hidden)} แถว` : '',
    merged.unwanted > 0 ? `ข้ามแถวที่ช่อง "ต้องการ" ว่าง ${num(merged.unwanted)} แถว` : '',
    merged.blank > 0 ? `${num(merged.blank)} แถวไม่มีรหัส` : '',
  ].filter(Boolean);

  const fileCard = () => (
    <Grid item xs={12}>
      <MDBox
        p={1.5}
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 2,
          opacity: fileBusy ? 0.5 : 1,
        }}
      >
        <MDBox display='flex' alignItems='center' gap={1}>
          {fileBusy ? <CircularProgress size={18} /> : <Icon color='success'>description</Icon>}
          <MDTypography
            variant='button'
            fontWeight='medium'
            title={file.name}
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fileBusy ? 'กำลังอ่านไฟล์ใหม่…' : file.name}
          </MDTypography>
          <MDButton
            size='small'
            variant='text'
            color='info'
            disabled={fileBusy}
            onClick={() => fileInput.current && fileInput.current.click()}
          >
            เปลี่ยนไฟล์
          </MDButton>
          <MDButton
            size='small'
            variant='text'
            color='error'
            disabled={fileBusy}
            onClick={() => onChange({ ...value, file: null })}
          >
            เอาออก
          </MDButton>
        </MDBox>
        {bookSheets.length === 1 ? (
          <MDTypography variant='caption' color='text' display='block'>
            รหัสจากคอลัมน์ {bookSheets[0].column}
            {bookSheets[0].header ? ` "${bookSheets[0].header}"` : ''} · {num(bookSheets[0].rows)}{' '}
            แถว
          </MDTypography>
        ) : (
          <MDBox mt={0.5}>
            <MDTypography variant='caption' color='text' fontWeight='medium' display='block'>
              ติ๊กชีตที่จะทำป้าย
            </MDTypography>
            {bookSheets.map((sh, i) => (
              <FormControlLabel
                key={sh.sheet}
                sx={{ display: 'flex', ml: 0, mr: 0 }}
                control={
                  <Checkbox
                    size='small'
                    checked={Boolean(file.picked[i])}
                    onChange={() => togglePick(i)}
                    disabled={fileBusy}
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <MDTypography variant='caption' color='text'>
                    {sh.sheet} · คอลัมน์ {sh.column}
                    {sh.header ? ` "${sh.header}"` : ''} ·{' '}
                    {num(
                      file.skipUnwanted && sh.want_col ? (sh.want_ids || []).length : sh.ids.length
                    )}{' '}
                    สินค้า
                    {Number(
                      file.skipUnwanted && sh.want_col ? sh.missing_want_count : sh.missing_count
                    ) > 0
                      ? ` · ไม่พบ ${num(
                          file.skipUnwanted && sh.want_col
                            ? sh.missing_want_count
                            : sh.missing_count
                        )}`
                      : ''}
                    {file.skipUnwanted
                      ? sh.want_col
                        ? ` · ช่องต้องการ: คอลัมน์ ${sh.want_col}`
                        : ' · ไม่มีช่องต้องการ ทำทุกแถว'
                      : ''}
                  </MDTypography>
                }
              />
            ))}
          </MDBox>
        )}
        {bookSheets.some((sh, i) => sh.want_col && file.picked[i]) ? (
          <FormControlLabel
            sx={{ display: 'flex', ml: 0, mr: 0, mt: 0.5 }}
            control={
              <Checkbox
                size='small'
                checked={Boolean(file.skipUnwanted)}
                onChange={() =>
                  onChange({ ...value, file: { ...file, skipUnwanted: !file.skipUnwanted } })
                }
                disabled={fileBusy}
                sx={{ p: 0.5 }}
              />
            }
            label={
              <MDTypography variant='caption' color='text' fontWeight='medium'>
                ทำป้ายเฉพาะแถวที่ช่อง &quot;ต้องการ&quot; มีจำนวน (ข้ามแถวที่ว่าง =
                ไม่ได้สั่งเข้าสาขา)
              </MDTypography>
            }
          />
        ) : null}
        {(file.no_code_sheets || []).length ? (
          <MDTypography variant='caption' color='text' display='block'>
            ชีตที่ไม่มีรหัสสินค้า: {file.no_code_sheets.join(', ')}
          </MDTypography>
        ) : null}
        {notes.length ? (
          <MDTypography variant='caption' color='text' display='block'>
            {notes.join(' · ')}
          </MDTypography>
        ) : null}
        {codeLine('ไม่พบในระบบ', merged.missing, merged.missingCount)}
        {merged.missing.some((x) => /E\+/i.test(x)) ? (
          <MDTypography variant='caption' color='warning' display='block'>
            บาร์โค้ดบางตัวถูก Excel ย่อเป็น 8.85E+12 — ตั้งคอลัมน์เป็นข้อความ (Text) แล้วเซฟใหม่
          </MDTypography>
        ) : null}
        {codeLine('ปิดขาย/ไม่มีบาร์โค้ดหลัก', merged.closed, merged.closedCount)}
        <MDBox mt={1}>
          {fileTotal > fileMax ? (
            <MDTypography variant='caption' color='error' fontWeight='medium'>
              ได้ {num(fileTotal)} สินค้า เกินเพดาน {num(fileMax)} · เอาชีตที่ไม่ใช้ออก หรือแบ่งไฟล์
            </MDTypography>
          ) : (
            <MDTypography
              variant='caption'
              color={fileTotal ? 'text' : 'warning'}
              fontWeight='medium'
            >
              {fileTotal
                ? `ได้ ${num(fileTotal)} สินค้า เรียงตามไฟล์ · ${num(filePages)} หน้า${
                    fileSets > 1 ? ` · แบ่ง ${fileSets} ชุด (ชุดละไม่เกิน ${num(perSet)})` : ''
                  }`
                : bookSheets.length > 1
                ? 'ติ๊กอย่างน้อย 1 ชีต'
                : RANK_ERROR_TEXT.import_no_products}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Grid>
  );

  /* mode (หน้าต่างสร้างชุด 3 ขั้น · 2 พ.ย. 2569): 'rank' = เฉพาะหมวด/A-B-C · 'file' = เฉพาะไฟล์ · 'empty' = ไม่แสดง · ไม่ส่ง = แบบเดิม (เลือกเองในนี้) */
  if (mode === 'empty') return null;
  const showCat = mode ? mode === 'rank' : !file;
  const showUpload = mode ? mode === 'file' && !file : !file && !value.cat_id;
  return (
    <MDBox
      mt={mode ? 0 : 3}
      pt={mode ? 0 : 2}
      sx={mode ? {} : { borderTop: '1px solid', borderColor: 'grey.300' }}
    >
      <input
        ref={fileInput}
        type='file'
        accept='.xlsx,.xls,.csv'
        hidden
        aria-label='เลือกไฟล์รหัสสินค้า'
        onChange={readFile}
      />
      {mode ? null : file ? (
        <MDTypography variant='button' fontWeight='medium' display='block' mb={1}>
          สินค้าจากไฟล์
        </MDTypography>
      ) : (
        <MDBox display='flex' alignItems='center' gap={0.5} mb={1}>
          <MDTypography variant='button' fontWeight='medium'>
            เลือกสินค้าจากยอดขาย
          </MDTypography>
          <Tooltip title='A/B/C คิดจากยอดขายของสาขาในหมวดที่เลือก (A = ยอดสะสม 80% แรก · B = 15% ถัดมา · C = ที่เหลือ) · นับเฉพาะสินค้าที่ขายได้ในช่วงนั้น · เรียงขายดีก่อน'>
            <Icon fontSize='small' color='action' aria-label='วิธีคิด A/B/C' tabIndex={0}>
              info_outline
            </Icon>
          </Tooltip>
        </MDBox>
      )}
      {/* ป้ายช่องของ theme สูง 0.9 บรรทัด + ตัดส่วนเกิน: หาง g y และสระล่างหาย */}
      <Grid container spacing={2} sx={{ '& .MuiInputLabel-root': { lineHeight: 1.3 } }}>
        {file ? fileCard() : null}
        {showCat ? (
          <Grid item xs={12}>
            <MDSelect
              label='หมวด (Category)'
              labelDefault={mode ? 'เลือกหมวด' : 'ไม่เลือก · สร้างป้ายเปล่า'}
              data={opt.categories}
              value={value.cat_id}
              onChange={(e) => onChange({ ...value, cat_id: e.target.value, subcat_id: '0' })}
            />
          </Grid>
        ) : null}
        {showUpload ? (
          <Grid item xs={12}>
            <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
              {mode ? null : (
                <MDTypography variant='caption' color='text'>
                  หรือ
                </MDTypography>
              )}
              <MDButton
                size={mode ? 'medium' : 'small'}
                variant={mode ? 'contained' : 'outlined'}
                color='info'
                disabled={fileBusy}
                onClick={() => fileInput.current && fileInput.current.click()}
              >
                {fileBusy ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <Icon>upload_file</Icon>
                )}
                &nbsp;{fileBusy ? 'กำลังอ่านไฟล์…' : 'อัปไฟล์รหัสสินค้า'}
              </MDButton>
              <MDTypography variant='caption' color='text'>
                Excel หรือ CSV · รหัส RT หรือบาร์โค้ด · ไม่เกิน 3,000 สินค้า
                {mode ? ' · ป้ายเรียงตามแถวในไฟล์' : ''}
              </MDTypography>
            </MDBox>
          </Grid>
        ) : null}
        {fileError ? (
          <Grid item xs={12} pt='0 !important'>
            <MDTypography variant='caption' color='error' role='alert'>
              {fileError}
            </MDTypography>
          </Grid>
        ) : null}
        {!file && value.cat_id ? (
          <>
            <Grid item xs={7}>
              <MDSelect
                label='หมวดย่อย (Group)'
                labelDefault='ทุกหมวดย่อย'
                valueDefault='0'
                data={groups}
                value={value.subcat_id}
                onChange={(e) => onChange({ ...value, subcat_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={5}>
              <MDInput
                select
                fullWidth
                variant='standard'
                label='ยอดขายย้อนหลัง'
                value={value.months}
                onChange={(e) => onChange({ ...value, months: e.target.value })}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m} เดือน
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12}>
              <MDBox display='flex' gap={1} flexWrap='wrap' role='group' aria-label='เลือก A/B/C'>
                {LETTERS.map((k) => {
                  const on = value.abc.includes(k);
                  const n = counts ? counts.counts[k] : null;
                  return (
                    <Chip
                      key={k}
                      label={n === null ? k : `${k} · ${num(n)}`}
                      color={on ? 'info' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      icon={on ? <Icon>check</Icon> : undefined}
                      onClick={() => toggle(k)}
                      aria-pressed={on}
                      sx={{ minWidth: 84, fontWeight: 600 }}
                    />
                  );
                })}
              </MDBox>
            </Grid>
            <Grid item xs={12}>
              <MDInput
                select
                fullWidth
                variant='standard'
                label='จะพิมพ์กี่แผ่น (ดูจากหน้าชั้น)'
                value={value.sheets || '0'}
                onChange={(e) => onChange({ ...value, sheets: e.target.value })}
              >
                <MenuItem value='0'>ทั้งหมดในเกรดที่เลือก</MenuItem>
                {Array.from({ length: MAX_SHEETS }, (_, i) => String(i + 1)).map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} แผ่น · ตัวขายดีสุด {num(Number(n) * perPage)} ป้าย
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
            <Grid item xs={12}>
              {summary()}
            </Grid>
          </>
        ) : null}
      </Grid>
    </MDBox>
  );
}

TagRankPicker.defaultProps = {
  open: true,
  branchId: '',
  templates: [],
  templateId: '0',
  mode: null,
};

TagRankPicker.propTypes = {
  open: PropTypes.bool,
  token: PropTypes.string.isRequired,
  branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  templates: PropTypes.arrayOf(PropTypes.object),
  templateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.shape({
    cat_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subcat_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    months: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    abc: PropTypes.arrayOf(PropTypes.string),
    sheets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    file: PropTypes.shape({
      name: PropTypes.string,
      picked: PropTypes.arrayOf(PropTypes.bool),
      skipUnwanted: PropTypes.bool,
      no_code_sheets: PropTypes.arrayOf(PropTypes.string),
      max_import: PropTypes.number,
      sheets: PropTypes.arrayOf(
        PropTypes.shape({
          sheet: PropTypes.string,
          column: PropTypes.string,
          header: PropTypes.string,
          rows: PropTypes.number,
          ids: PropTypes.arrayOf(PropTypes.number),
          missing: PropTypes.arrayOf(PropTypes.string),
          missing_count: PropTypes.number,
          closed: PropTypes.arrayOf(PropTypes.string),
          closed_count: PropTypes.number,
          missing_want: PropTypes.arrayOf(PropTypes.string),
          missing_want_count: PropTypes.number,
          closed_want: PropTypes.arrayOf(PropTypes.string),
          closed_want_count: PropTypes.number,
          dup: PropTypes.number,
          multi: PropTypes.number,
          hidden: PropTypes.number,
          blank: PropTypes.number,
          want_col: PropTypes.string,
          want_ids: PropTypes.arrayOf(PropTypes.number),
          unwanted: PropTypes.number,
        })
      ),
    }),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onStatus: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['rank', 'file', 'empty']),
};

export default TagRankPicker;
